import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { CustomDialog } from '../CustomDialog';
import { useVideoCardSide } from './useVideoCardSide';
import { VideoCardSideProps, ThumbnailImageProps } from './types';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import { hasSpecialtyAssigned } from '../../utils/videoSpecialtyStatus';
import { formatDuration, formatDate, getElapsedTime } from './utils';
import type { Swiper as SwiperType } from 'swiper';
import { getThumbnailUrl, getDefaultThumbnailUrl, isThumbnailPending } from '../../utils/thumbnailUtils';
import { KebabMenu } from '../common/KebabMenu';
import { normalizeVideoImagePath } from '../../utils/videoImagePath';
import { VISIBILITY_BADGE_LABELS } from '../../utils/visibilityLabels';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import styles from './VideoCardSide.module.css';

// サムネイル画像コンポーネント（画像サイズをコンテナに合わせて表示）
const ThumbnailImage = ({ src, alt, defaultStyle }: ThumbnailImageProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  
  const handleError = () => {
    setHasError(true);
  };
  
  return (
    <img
      src={hasError ? getDefaultThumbnailUrl() : src}
      alt={alt}
      style={{
        ...defaultStyle,
        width: '100%',
        height: '100%',
        objectFit: (defaultStyle && defaultStyle.objectFit) || 'contain',
        objectPosition: 'center',
      }}
      data-testid="video-card-side__thumbnail"
      onError={handleError}
    />
  );
};

/**
 * 動画カードコンポーネント（サイドバー表示用）
 * 横並び表示に最適化されたデザインの動画カード
 */
export const VideoCardSide = (props: VideoCardSideProps) => {
  const {
    video,
    showUserIcon = false,
    hideVisibilityToggle = false
  } = props;

  // 接続元判定: サーバー API /api/access/origin を起動時1回取得。
  // isLoading 中は isExternal=false（活性のままサーバー 403 に委ねる / UX 原則）。
  // isExternal=true の場合、ケバブメニューのDLボタンを disabled にする（UX R1-a）。
  const { isExternal } = useOriginAccess();
  // 診療科未設定の動画は公開系メニュー（院内公開にする／外部公開にする）を disabled にする。
  // 判定は3種のカードで共通のユーティリティに集約している（videoSpecialtyStatus.ts）。
  const hasSpecialty = hasSpecialtyAssigned(video);

  // 処理中動画の判定: is_processingフラグまたはIDがsession_で始まる場合
  const isVideoProcessing = video.is_processing || String(video.id).startsWith('session_');

  const {
    hasBeenSwiped,
    isFavorite,
    dialogOpen,
    dialogConfig,
    swiperRef,
    uploaderIconUrl,
    channelLabel,
    isOwner,
    visibility,
    isPrivate,
    sortedImages,
    hasMultipleImages,
    handleContainerClick,
    handleSlideChange,
    handleTouchStart,
    handleAutoplayStart,
    setDialogOpen,
    // ケバブメニュー関連
    kebabMenu
  } = useVideoCardSide(props);

  return (
    <div
      className={styles['video-card-side-container']}
      onClick={handleContainerClick}
      data-testid="video-card-side__container"
    >
      {/* サムネイル部分 */}
      <div className={styles['video-card-side-thumbnail-wrapper']}>
        {sortedImages && sortedImages.length > 0 ? (
          <>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              slidesPerView={1}
              loop={true}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
                enabled: true,
              }}
              pagination={{ clickable: true }}
              className={styles['video-card-side-swiper']}
              passiveListeners={true}
              touchRatio={1}
              touchStartPreventDefault={false}
              onSlideChange={handleSlideChange}
              onTouchStart={handleTouchStart}
              onAutoplayStart={handleAutoplayStart}
              autoplay={!hasBeenSwiped ? {
                delay: 3000,
                disableOnInteraction: false
              } : false}
              onSwiper={(swiper: SwiperType) => {
                if (swiperRef) {
                  swiperRef.current = swiper;
                }
              }}
              data-testid="video-card-side__swiper"
            >
              {sortedImages.map((img, idx) => (
                <SwiperSlide key={idx}>
                  <div className={styles['video-card-side-thumbnail-inner-wrapper']}>
                    <ThumbnailImage
                      src={normalizeVideoImagePath(img.image_path)}
                      alt={img.description || '動画画像'}
                      defaultStyle={{ objectFit: 'contain' }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {/* スワイプインジケーターを追加（複数画像の場合のみ表示、かつスワイプされていない場合） */}
            {hasMultipleImages && !hasBeenSwiped && (
              <div className={styles['video-card-side-swipe-indicator']}>
                <span className={styles['video-card-side-swipe-icon']}>← スワイプ →</span>
              </div>
            )}
          </>
        ) : video.thumbnail ? (
          <div className={styles['video-card-side-thumbnail-inner-wrapper']}>
            <ThumbnailImage
              src={getThumbnailUrl(video.thumbnail) || getDefaultThumbnailUrl()}
              alt={video.name || ''}
              defaultStyle={{ objectFit: 'cover' }}
            />
          </div>
        ) : isThumbnailPending(video) ? (
          <div className={styles['video-card-side-spinner-container']}>
            <div className={styles['video-card-side-spinner']}></div>
          </div>
        ) : (
          <div className={styles['video-card-side-thumbnail-inner-wrapper']}>
            <ThumbnailImage
              src={getDefaultThumbnailUrl()}
              alt={video.name || ''}
              defaultStyle={{ objectFit: 'cover' }}
            />
          </div>
        )}
        
        {/* 再生時間 */}
        <div className={styles['video-card-side-duration']} data-testid="video-card-side__duration">
          {formatDuration(video.duration)}
        </div>

        {/* お気に入りアイコン（表示のみ、ボタン機能なし） */}
        {isFavorite && (
          <div className={styles['video-card-side-favorite-icon']}>
            <span role="img" aria-label="お気に入り">★</span>
          </div>
        )}

        {/* 資料バッジ */}
        {!!video.materials?.length && (
          <span className={styles['video-card-side-materials-badge']} data-testid="video-card-side__materials-badge">
            資料 {video.materials.length}
          </span>
        )}
      </div>

      {/* 動画情報部分 */}
      <div className={styles['video-card-side-info-wrapper']}>
        <div className={styles['video-card-side-info']}>
          <div className={styles['video-card-side-title-wrapper']}>
            <div 
              className={styles['video-card-side-title']} 
              title={video.title || video.name || ''}
              data-testid="video-card-side__title"
            >
              {video.title || video.name}
            </div>
            {/* ケバブメニュー */}
            <KebabMenu
              isOpen={kebabMenu.isMenuOpen}
              position={kebabMenu.menuPosition}
              onClose={kebabMenu.closeMenu}
              isFavorite={isFavorite}
              isOwner={isOwner}
              isPrivate={isPrivate}
              currentVisibility={visibility}
              isProcessing={isVideoProcessing}
              hideVisibilityToggle={hideVisibilityToggle || isVideoProcessing}
              hideFavorite={isVideoProcessing}
              hideEdit={false}
              isExternal={isExternal}
              hasSpecialty={hasSpecialty}
              onFavoriteClick={kebabMenu.handleFavoriteMenuClick}
              onVisibilityClick={kebabMenu.handleVisibilityMenuClick}
              onEditClick={kebabMenu.handleEditMenuClick}
              onDownloadClick={isVideoProcessing ? undefined : kebabMenu.handleDownloadMenuClick}
              onDeleteClick={kebabMenu.handleDeleteMenuClick}
              menuRef={kebabMenu.menuRef}
              buttonRef={kebabMenu.buttonRef}
              onToggle={kebabMenu.toggleMenu}
              testIdPrefix="video-card-side"
            />
          </div>
          <div className={styles['video-card-side-meta-wrapper']}>
            {showUserIcon && (
              <img
                src={uploaderIconUrl}
                alt="Uploader Icon"
                className={styles['video-card-side-avatar']}
                data-testid="video-card-side__uploader-icon"
              />
            )}
            <div className={styles['video-card-side-meta-text']}>
              <p 
                className={styles['video-card-side-channel-name']} 
                title={channelLabel}
                data-testid="video-card-side__channel-name"
              >
                {channelLabel}
              </p>
              <p
                className={styles['video-card-side-details']}
                data-testid="video-card-side__details"
              >
                {isPrivate
                  ? formatDate(video.created_at)
                  : getElapsedTime(video.published_at || video.created_at)}
                {video.surgery_date && (
                  <>
                    {' '}手術日 {formatDate(video.surgery_date)}
                  </>
                )}
              </p>
            </div>
            
            {/* 公開状態バッジ */}
            {isOwner && !hideVisibilityToggle && (
              video.is_processing ? (
                <span
                  className={styles['video-card-side-visibility-badge']}
                  data-visibility="processing"
                >
                  変換中
                </span>
              ) : (
                <span
                  className={styles['video-card-side-visibility-badge']}
                  data-visibility={visibility}
                >
                  {VISIBILITY_BADGE_LABELS[visibility]}
                </span>
              )
            )}
          </div>
        </div>
      </div>


      {/* カスタムダイアログ */}
      <CustomDialog
        isOpen={dialogOpen}
        title={dialogConfig.title}
        message={dialogConfig.message}
        confirmText={dialogConfig.confirmText}
        cancelText="キャンセル"
        onConfirm={() => {
          setDialogOpen(false);
          dialogConfig.onConfirm();
        }}
        onCancel={() => setDialogOpen(false)}
        type={dialogConfig.type}
      />

    </div>
  );
};

export default VideoCardSide; 
